/**
 * In-process event bus for session-expired signals.
 *
 * Anywhere we used to call `auth.signOut()` on a transient auth failure,
 * we now emit on this bus instead. The overlay listens and decides whether
 * to silently refresh the token or surface interactive UI.
 *
 * Per Inv 3 ("auth expiry is not logout"), no listener of this event
 * should call `signOut()` directly — only the user's explicit "Sign out"
 * click does that, via the existing auth-service path.
 */

const EVENT_NAME = 'polymyth:session-expired';
const target = new EventTarget();

export interface SessionExpiredDetail {
  /** Short tag identifying the source (e.g. 'api-401', 'ws-auth'). For logs. */
  reason: string;
}

export function emitSessionExpired(reason: string): void {
  target.dispatchEvent(new CustomEvent<SessionExpiredDetail>(EVENT_NAME, { detail: { reason } }));
}

/**
 * Subscribe to session-expired events. Returns an unsubscribe function.
 */
export function onSessionExpired(handler: (detail: SessionExpiredDetail) => void): () => void {
  const listener = (event: Event) => {
    const customEvent = event as CustomEvent<SessionExpiredDetail>;
    handler(customEvent.detail);
  };
  target.addEventListener(EVENT_NAME, listener);
  return () => target.removeEventListener(EVENT_NAME, listener);
}
